import type { TFunction } from "i18next";
import type { NavigateFunction } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Ban,
  CheckCircle,
  Clock,
  Copy,
  Eye,
  FileDown,
  FileMinus,
  FileText,
  RotateCw,
  Trash2,
  XCircle,
} from "lucide-react";

import type { ElectronicInvoice } from "@fiscalo/shared-types";
import {
  CONSUMER_PLACEHOLDER_NAME,
  isBaseInvoiceType,
  isCreditNote,
  isDebitNote,
} from "@fiscalo/shared-types";

import type { TableAction } from "@/components/shared/TableActionsMenu";
import { downloadInvoicePdf } from "@/services/invoiceService";

export const CFE_TYPE_CODES = [
  "101",
  "102",
  "103",
  "111",
  "112",
  "121",
  "122",
  "123",
] as const;

export function formatComprobanteLabel(
  cfeType: string | undefined,
  internalNumber: number | undefined,
  t: TFunction
): string {
  if (!cfeType && internalNumber == null) return "—";
  const typeLabel =
    cfeType && CFE_TYPE_CODES.includes(cfeType as (typeof CFE_TYPE_CODES)[number])
      ? t(`invoices.cfeTypes.${cfeType}`, cfeType)
      : cfeType ?? "—";
  if (internalNumber != null) return `${typeLabel} ${internalNumber}`;
  return typeLabel;
}

export type ChildKind = "creditNote" | "rejectedAncestor";

export type ReissueRef = {
  /** "successorOf" → this row is the live re-issue of `targetId`; "predecessorOf" → this row is the rejected ancestor of `targetId`. */
  direction: "successorOf" | "predecessorOf";
  targetId: string;
  targetCfeType: string | undefined;
  targetInternalNumber: number | undefined;
};

export type GroupedInvoiceRow<T extends ElectronicInvoice> = {
  inv: T;
  isChild: boolean;
  childKind?: ChildKind;
  /** Set on a primary row when one or more rejected ancestors exist (drives the disclosure toggle). */
  hasRejectedAncestor?: boolean;
  /** Number of rejected ancestors hidden under this primary, for plural-aware toggle copy. */
  rejectedAncestorCount?: number;
  /** Cross-link to the related invoice (rendered as a chip on the primary row only). */
  reissueRef?: ReissueRef;
};

/**
 * Group invoices for list display. Two grouping rules:
 *
 *  1. Credit/Debit notes whose referencedInvoiceId is on the page → child of
 *     the base invoice (NC/ND rendered indented under base).
 *  2. Re-issue chains: builds the connected component of every invoice
 *     linked through `reissuedFromInvoiceId` (parent <-> child edges treated
 *     as undirected). The live successor in each component (status='sent'
 *     and dgiStatus != 'rejected') becomes the primary row; every other
 *     member — direct parent, transitive ancestor, or rejected sibling
 *     sharing a source — is attached underneath as a "rejectedAncestor"
 *     child. Caller hides them behind a disclosure toggle by default.
 *
 * Backend pre-fetches related invoices across pagination boundaries, so this
 * function only reorders what's already in `invoices`.
 */
export function groupInvoicesWithChildren<T extends ElectronicInvoice>(
  invoices: readonly T[]
): Array<GroupedInvoiceRow<T>> {
  const placedAsChild = new Set<string>();
  type ChildEntry = { inv: T; childKind: ChildKind; reissueRef?: ReissueRef };
  const childrenByParent = new Map<string, ChildEntry[]>();
  const reissueRefByPrimary = new Map<string, ReissueRef>();
  const ancestorCountByPrimary = new Map<string, number>();

  const idSet = new Set(invoices.map((i) => i.id));
  const byId = new Map(invoices.map((i) => [i.id, i]));

  const addChild = (parentId: string, entry: ChildEntry): void => {
    const existing = childrenByParent.get(parentId) ?? [];
    existing.push(entry);
    childrenByParent.set(parentId, existing);
    placedAsChild.add(entry.inv.id);
  };

  for (const inv of invoices) {
    const ref = (inv as unknown as Record<string, unknown>).referencedInvoiceId as
      | string
      | undefined;
    if (
      ref &&
      idSet.has(ref) &&
      (isCreditNote(inv.cfeType ?? "") || isDebitNote(inv.cfeType ?? ""))
    ) {
      addChild(ref, { inv, childKind: "creditNote" });
    }
  }

  // Build reissue DAG (child -> parent + parent -> children) excluding any
  // node already pulled in as a credit-note child.
  const reissueParent = new Map<string, string>();
  const reissueChildren = new Map<string, string[]>();
  for (const inv of invoices) {
    if (placedAsChild.has(inv.id)) continue;
    const reissuedFrom = (inv as unknown as Record<string, unknown>)
      .reissuedFromInvoiceId as string | undefined;
    if (!reissuedFrom || !idSet.has(reissuedFrom)) continue;
    if (placedAsChild.has(reissuedFrom)) continue;
    reissueParent.set(inv.id, reissuedFrom);
    const arr = reissueChildren.get(reissuedFrom) ?? [];
    arr.push(inv.id);
    reissueChildren.set(reissuedFrom, arr);
  }

  // Walk connected components and pick a primary per component.
  const seen = new Set<string>();
  const issueDateMs = (inv: T): number =>
    inv.issueDate ? new Date(inv.issueDate).getTime() : 0;
  const isLive = (inv: T): boolean =>
    inv.status === "sent" &&
    ((inv as unknown as Record<string, unknown>).dgiStatus as string | undefined) !==
      "rejected";

  for (const inv of invoices) {
    const startId = inv.id;
    if (seen.has(startId) || placedAsChild.has(startId)) continue;
    if (!reissueParent.has(startId) && !reissueChildren.has(startId)) continue;

    const componentIds = new Set<string>();
    const queue = [startId];
    while (queue.length) {
      const id = queue.shift() as string;
      if (componentIds.has(id)) continue;
      componentIds.add(id);
      const p = reissueParent.get(id);
      if (p && !componentIds.has(p)) queue.push(p);
      const ch = reissueChildren.get(id) ?? [];
      for (const c of ch) if (!componentIds.has(c)) queue.push(c);
    }
    if (componentIds.size <= 1) {
      componentIds.forEach((id) => seen.add(id));
      continue;
    }

    componentIds.forEach((id) => seen.add(id));
    const members = Array.from(componentIds, (id) => byId.get(id) as T);
    const live = members.find(isLive);
    let primary: T;
    if (live) {
      primary = live;
    } else {
      const sorted = [...members].sort((a, b) => {
        const dd = issueDateMs(b) - issueDateMs(a);
        if (dd !== 0) return dd;
        return (b.internalNumber ?? 0) - (a.internalNumber ?? 0);
      });
      primary = sorted[0];
    }

    const directParentId = reissueParent.get(primary.id);
    if (directParentId) {
      const src = byId.get(directParentId);
      // Only cross-link to a REJECTED/superseded predecessor. A live (accepted)
      // source means this link came from "Replicar" (a fresh independent
      // invoice), not a reissue of a rejected CFE — no chip, no grouping.
      if (src && !isLive(src)) {
        reissueRefByPrimary.set(primary.id, {
          direction: "successorOf",
          targetId: src.id,
          targetCfeType: src.cfeType,
          targetInternalNumber: src.internalNumber,
        });
      }
    }

    // Nest ONLY non-live members (rejected / superseded attempts). A confirmed,
    // sent CFE is a real document — it must never be hidden under another as a
    // "rejected intento" (this is the SALKANTAI 41-under-44 bug: two accepted
    // e-Tickets linked by a Replicar, both live). Live non-primary members fall
    // through and render as their own top-level rows.
    const ancestors = members
      .filter((m) => m.id !== primary.id && !isLive(m))
      .sort((a, b) => {
        const dd = issueDateMs(b) - issueDateMs(a);
        if (dd !== 0) return dd;
        return (b.internalNumber ?? 0) - (a.internalNumber ?? 0);
      });
    for (const anc of ancestors) {
      addChild(primary.id, { inv: anc, childKind: "rejectedAncestor" });
    }
    if (ancestors.length > 0) {
      ancestorCountByPrimary.set(primary.id, ancestors.length);
    }
  }

  const out: Array<GroupedInvoiceRow<T>> = [];
  for (const inv of invoices) {
    if (placedAsChild.has(inv.id)) continue;
    const ancestorCount = ancestorCountByPrimary.get(inv.id);
    out.push({
      inv,
      isChild: false,
      hasRejectedAncestor: ancestorCount ? true : undefined,
      rejectedAncestorCount: ancestorCount,
      reissueRef: reissueRefByPrimary.get(inv.id),
    });
    const children = childrenByParent.get(inv.id);
    if (children) {
      for (const c of children) {
        out.push({
          inv: c.inv,
          isChild: true,
          childKind: c.childKind,
          reissueRef: c.reissueRef,
        });
      }
    }
  }
  return out;
}

/**
 * Single source of truth for the user-facing status displayed on issued
 * invoices. Maps invoice state to one of four labels:
 * - draft                       → yellow "Borrador"
 * - sent + annulledAt set        → red "Anulada" (terminal; the número is void)
 * - sent + dgiStatus rejected    → red "Rechazada"
 * - sent (anything else)         → green "Enviada"
 *
 * Annulment and rejection are surfaced because they change what the document
 * IS; other DGI states (pending/sent/confirmed) stay folded under "Enviada".
 *
 * Exception: a rejected NC/DN that the backend is auto-healing with a global
 * reference (`autoReissuePending`) is transient — it will be replaced by an
 * accepted reissue — so we NEVER show its intermediate "Rechazada"; it reads as
 * in-flight ("Enviada") until the reissue lands (grouping then hides it under
 * the accepted successor) or the heal terminally fails (backend clears the
 * flag → this falls through to "rejected").
 */
export function resolveInvoiceDisplayStatus(
  inv: ElectronicInvoice
): "draft" | "sent" | "rejected" | "anulado" {
  if (inv.status === "draft") return "draft";
  const rec = inv as unknown as Record<string, unknown>;
  if (rec.annulledAt != null) return "anulado";
  if ((rec.dgiStatus as string | undefined) === "rejected") {
    if (rec.autoReissuePending === true) return "sent";
    return "rejected";
  }
  return "sent";
}

/**
 * Name to show for a consumidor-final receiver, or null when the buyer really
 * is anonymous.
 *
 * The placeholder RUT means "no tax ID required", NOT "no name": an API
 * integration can name its buyer (Gestionalo sends `{ rut: 000000000000, name:
 * "Luis Cal" }`). That name is not cosmetic — it travels to DGI as RznSocRecep
 * and prints in the PDF's NOMBRE field — so the UI must not replace it with the
 * generic "Consumidor final" label.
 *
 * Returns null for the placeholder name itself, which the backend stamps when
 * the receiver was left blank (invoice-from-dto.builder.ts) — there the generic
 * label IS the whole truth.
 */
export function resolveConsumerFinalName(
  name: string | null | undefined
): string | null {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) return null;
  return trimmed.toLowerCase() === CONSUMER_PLACEHOLDER_NAME.toLowerCase()
    ? null
    : trimmed;
}

export function statusVariant(
  status: string
): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "accepted":
    case "confirmed":
      return "success";
    case "rejected":
    case "anulado":
      return "danger";
    case "sent":
      return "success";
    case "pending":
    case "pending_submission":
      return "info";
    case "draft":
      return "warning";
    case "cancelled":
    default:
      return "info";
  }
}

export function statusIcon(status: string): typeof CheckCircle {
  switch (status) {
    case "accepted":
    case "sent":
      return CheckCircle;
    case "rejected":
      return XCircle;
    case "anulado":
      return Ban;
    case "draft":
      return Clock;
    default:
      return Clock;
  }
}

/**
 * Whether the row can be annulled: an issued CFE that is NOT a DGI-accepted valid
 * document and isn't already annulled. Accepted CFEs (dgiStatus 'confirmed') are
 * reversed with a Nota de Crédito instead; the backend also enforces this (409).
 */
export function canAnnulInvoice(inv: ElectronicInvoice): boolean {
  const rec = inv as unknown as Record<string, unknown>;
  if (inv.status !== "sent") return false;
  if (rec.annulledAt != null) return false;
  return (rec.dgiStatus as string | undefined) !== "confirmed";
}

/** Whether the list row can open the credit-note flow (sent+confirmed base CFE; no NC yet; optional API flag). */
export function canReturnForCreditNote(
  inv: ElectronicInvoice,
  invoicingBlocked: boolean
): boolean {
  const invRecord = inv as unknown as Record<string, unknown>;
  const dgiStatus = invRecord.dgiStatus as string | undefined;
  if (invoicingBlocked || inv.status !== "sent" || dgiStatus !== "confirmed") return false;
  if (!isBaseInvoiceType(inv.cfeType ?? "")) return false;
  if (invRecord.hasRelatedCreditNotes === true) return false;
  const ext = inv as ElectronicInvoice & { canIssueCreditNote?: boolean };
  return ext.canIssueCreditNote !== false;
}

export function formatTotal(
  currency: string | undefined,
  amount: number | undefined
): string {
  if (amount == null) return "—";
  const curr = currency ?? "UYU";
  return `${curr} ${Number(amount).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Invoice is waiting to be sent to DGI — no longer needed with auto-submit, but kept for edge cases. */
export function canQueueInvoiceSubmission(inv: ElectronicInvoice): boolean {
  const dgiStatus = (inv as unknown as Record<string, unknown>).dgiStatus as string | undefined;
  return dgiStatus === "pending" && inv.status === "sent";
}

export function buildInvoiceListActions(
  inv: ElectronicInvoice,
  options: {
    t: TFunction;
    navigate: NavigateFunction;
    invoicingBlocked: boolean;
    onQueueSubmission?: (invoiceId: string) => void;
    queueSubmissionLoadingId?: string | null;
    /** Rejected invoices: navigate to reissue flow. */
    onReissueRejected?: (invoiceId: string, cfeType?: string, referencedInvoiceId?: string) => void;
    reissueLoadingId?: string | null;
    /** Anular a CFE número (reports it as anulado in the Reporte Diario). */
    onAnnul?: (invoiceId: string) => void;
    annulLoadingId?: string | null;
    /** Delete a draft (status='draft' only) directly from the list. */
    onDeleteDraft?: (invoiceId: string) => void;
    deleteDraftLoadingId?: string | null;
  }
): TableAction[] {
  const {
    t,
    navigate,
    invoicingBlocked,
    onQueueSubmission,
    queueSubmissionLoadingId,
    onReissueRejected,
    reissueLoadingId,
    onAnnul,
    annulLoadingId,
    onDeleteDraft,
    deleteDraftLoadingId,
  } = options;
  const dgiStatus = (inv as unknown as Record<string, unknown>).dgiStatus as string | undefined;
  const isDgiRejected = dgiStatus === "rejected";
  const dgiSt = (inv as unknown as Record<string, unknown>).dgiStatus as string | undefined;
  const isAccepted = inv.status === "sent" && dgiSt === "confirmed";
  const cfe = inv.cfeType ?? "";
  const isNoteType = isCreditNote(cfe) || isDebitNote(cfe);
  const invRecord = inv as unknown as Record<string, unknown>;
  const hasAdjustments =
    invRecord.hasRelatedCreditNotes === true ||
    invRecord.hasRelatedDebitNotes === true;

  return [
    {
      label: t("invoices.list.viewInvoice", "View invoice"),
      icon: Eye,
      onClick: () => navigate(`/invoices/${inv.id}`),
    },
    ...(canQueueInvoiceSubmission(inv) && onQueueSubmission
      ? [
          {
            label: t("invoices.list.queueSubmission", "Send to DGI now"),
            icon: RotateCw,
            onClick: () => {
              onQueueSubmission(inv.id);
            },
            disabled: queueSubmissionLoadingId === inv.id,
          } as TableAction,
        ]
      : []),
    ...(hasAdjustments
      ? [
          {
            label: t("invoices.list.viewAdjustments", "View adjustments"),
            icon: FileText,
            onClick: () => navigate(`/invoices/${inv.id}`),
          } as TableAction,
        ]
      : []),
    ...(!invoicingBlocked && isAccepted && !isNoteType
      ? [
          {
            label: t("invoices.list.replicateInvoice", "Replicate"),
            icon: Copy,
            onClick: () =>
              navigate("/invoices/new", { state: { reissueFromId: inv.id } }),
          } as TableAction,
        ]
      : []),
    ...(canReturnForCreditNote(inv, invoicingBlocked)
      ? [
          {
            label: t("invoices.list.devolver", "Return"),
            icon: FileMinus,
            onClick: () => navigate(`/invoices/${inv.id}/credit-note`),
          } as TableAction,
        ]
      : []),
    {
      label: t("invoices.list.downloadPdf", "Download PDF"),
      icon: FileDown,
      onClick: () => {
        downloadInvoicePdf(inv.id).catch(() => {
          toast.error(t("invoices.create.downloadPdfError", "Could not download PDF."));
        });
      },
    },
    ...(!invoicingBlocked && isDgiRejected
      ? [
          {
            label: t("invoices.list.reissueInvoice", "Re-issue invoice"),
            icon: RotateCw,
            onClick: () => {
              if (onReissueRejected) {
                const refId = (inv as unknown as Record<string, unknown>).referencedInvoiceId as string | undefined;
                onReissueRejected(inv.id, inv.cfeType, refId);
              } else {
                navigate("/invoices/new", {
                  state: { reissueFromId: inv.id },
                });
              }
            },
            disabled: reissueLoadingId === inv.id,
          } as TableAction,
        ]
      : []),
    ...(!invoicingBlocked && onAnnul && canAnnulInvoice(inv)
      ? [
          {
            label: t("invoices.list.annul", "Anular"),
            icon: Ban,
            variant: "destructive",
            onClick: () => onAnnul(inv.id),
            disabled: annulLoadingId === inv.id,
          } as TableAction,
        ]
      : []),
    ...(inv.status === "draft" && onDeleteDraft
      ? [
          {
            label: t("invoices.list.deleteDraft", "Eliminar borrador"),
            icon: Trash2,
            variant: "destructive",
            onClick: () => onDeleteDraft(inv.id),
            disabled: deleteDraftLoadingId === inv.id,
          } as TableAction,
        ]
      : []),
  ];
}

export function invoiceHasAdjustments(inv: ElectronicInvoice): boolean {
  const invRecord = inv as unknown as Record<string, unknown>;
  return (
    invRecord.hasRelatedCreditNotes === true ||
    invRecord.hasRelatedDebitNotes === true
  );
}

export { isCreditNote, isDebitNote };
